import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiCheckCircle, FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { ADVANCE_RECOVERY_DATA } from "./worksReportsData";

const AdvanceRecoveryStatementReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(ADVANCE_RECOVERY_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalDisbursed = data.reduce((s, r) => s + (r.totalDisbursed || 0), 0);
    const totalRecovered = data.reduce((s, r) => s + (r.cumulativeRecovery || 0), 0);
    const totalOutstanding = data.reduce((s, r) => s + (r.outstandingBalance || 0), 0);
    const atRiskCount = data.filter((r) => r.recoveryStatus === "Delayed / Risk Alert").length;

    return {
      totalDisbursed,
      totalRecovered,
      totalOutstanding,
      atRiskCount,
    };
  }, [data]);

  const uniqueContractors = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.contractorName).filter(Boolean)));
  }, [data]);

  const getRecoveryChipType = (status) => {
    if (status === "Fully Recovered") {
      return "success";
    }
    if (status === "On Track") {
      return "info";
    }
    if (status === "Delayed / Risk Alert") {
      return "error";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Contractor Name",
        field: "contractorName",
        colId: "contractorName",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Work Order No. & Date",
        field: "workOrderNo",
        colId: "workOrderNo",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <div>
            <span
              style={{
                fontWeight: 700,
                fontFamily: "monospace",
                color: "var(--color-primary, #0b6462)",
              }}
            >
              {p.value}
            </span>
            <div style={{ color: "#64748b", fontSize: "11px" }}>Date: {p.data?.workOrderDate}</div>
          </div>
        ),
      },
      {
        headerName: "Initial Mob. Advance (PKR)",
        field: "initialMobAdvance",
        colId: "initialMobAdvance",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "PKR 0"),
      },
      {
        headerName: "Material Advance (PKR)",
        field: "materialAdvance",
        colId: "materialAdvance",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ color: p.value ? "#334155" : "#94a3b8" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Total Disbursed (PKR)",
        field: "totalDisbursed",
        colId: "totalDisbursed",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0f172a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Cumulative Recovered (PKR)",
        field: "cumulativeRecovery",
        colId: "cumulativeRecovery",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#16a34a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Outstanding Balance (PKR)",
        field: "outstandingBalance",
        colId: "outstandingBalance",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => {
          const val = p.value || 0;
          return (
            <span
              style={{
                fontWeight: 700,
                color: val > 0 ? "#dc2626" : "#16a34a",
              }}
            >
              PKR {Number(val).toLocaleString()}
            </span>
          );
        },
      },
      {
        headerName: "Recovery Schedule Status",
        field: "recoveryStatus",
        colId: "recoveryStatus",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => <StatusChip status={p.value} type={getRecoveryChipType(p.value)} />,
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.contractorName && row.contractorName.toLowerCase().includes(q)) ||
          (row.workOrderNo && row.workOrderNo.toLowerCase().includes(q)) ||
          (row.recoveryStatus && row.recoveryStatus.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.recoveryStatus !== statusFilter) {
        return false;
      }

      if (contractorFilter !== "all" && row.contractorName !== contractorFilter) {
        return false;
      }

      if (dateRange.from && row.workOrderDate && row.workOrderDate < dateRange.from) {
        return false;
      }
      if (dateRange.to && row.workOrderDate && row.workOrderDate > dateRange.to) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, contractorFilter, dateRange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Advance_Recovery_Statement_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Advance recovery statement exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL DISBURSED ADVANCES"
            value={`PKR ${(kpis.totalDisbursed / 1000000).toFixed(2)}M`}
            subtitle="Mob & material advances released"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="CUMULATIVE RECOVERED"
            value={`PKR ${(kpis.totalRecovered / 1000000).toFixed(2)}M`}
            subtitle="Amortized via running account bills"
            icon={FiRefreshCw}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="OUTSTANDING ADVANCE BALANCE"
            value={`PKR ${(kpis.totalOutstanding / 1000000).toFixed(2)}M`}
            subtitle="Pending recovery from future bills"
            icon={FiCheckCircle}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="AT-RISK / DELAYED RECOVERIES"
            value={`${kpis.atRiskCount} Accounts`}
            subtitle="Slow physical progress vs advance ratio"
            icon={FiAlertCircle}
            color="#dc2626"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Advance Recovery Statement"
            >
              Advance Recovery Statement
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search contractor, WO..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Recovery Status</option>
                  <option value="On Track">On Track</option>
                  <option value="Delayed / Risk Alert">Delayed / Risk Alert</option>
                  <option value="Fully Recovered">Fully Recovered</option>
                </select>
                <label>Recovery Status</label>
              </div>
            </div>

            {/* Contractor Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "220px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={contractorFilter}
                  onChange={(e) => setContractorFilter(e.target.value)}
                >
                  <option value="all">All Contractors</option>
                  {uniqueContractors.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <label>Contractor</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvanceRecoveryStatementReport;
